import {
  FileText,
  ClipboardCheck,
  AlertCircle,
  Search,
  Stethoscope,
  DollarSign,
  ShieldCheck,
  FileSearch,
  Calculator,
  HeartPulse,
  FileCheck,
  ShieldAlert,
  BadgeX,
  FileWarning,
  RefreshCcw,
  ShieldBan,
  BadgeDollarSign,
  ClipboardPen,
  ClipboardSignature,
  FilePlus2,
  FlaskConical,
  Landmark,
  Microscope,
  NotebookPen,
  Scissors,
  ScrollText,
  Shield,
  Smile,
  Syringe,
  TestTube2,
} from 'lucide-react';
import type { StarterPrompt } from './starter-prompts/starter-prompts';

export const STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: ClipboardCheck,
    title: 'Prior Authorization',
    description: 'Check requirements instantly',
    prompt:
      'What are the prior authorization requirements for a knee arthroscopy (CPT 29881) for a 65-year-old patient with Medicare?',
    gradient: 'from-blue-500/10 to-blue-600/10 hover:from-blue-500/20 hover:to-blue-600/20',
  },
  {
    icon: FileText,
    title: 'CPT Code Lookup',
    description: 'Find accurate billing codes',
    prompt:
      'What CPT codes should I use for a comprehensive metabolic panel with automated differential white blood cell count?',
    gradient: 'from-purple-500/10 to-purple-600/10 hover:from-purple-500/20 hover:to-purple-600/20',
  },
  {
    icon: AlertCircle,
    title: 'Claim Error Detection',
    description: 'Catch mistakes before submission',
    prompt:
      'Review this claim for errors: Patient has diagnosis J18.9 (Pneumonia) but procedure code is 99213 (Office visit level 3). Insurance is Aetna PPO. Any issues?',
    gradient: 'from-red-500/10 to-red-600/10 hover:from-red-500/20 hover:to-red-600/20',
  },
  {
    icon: Search,
    title: 'ICD-10 Code Mapping',
    description: 'Convert diagnoses to codes',
    prompt:
      'What is the correct ICD-10 code for "Type 2 diabetes with diabetic neuropathy and chronic kidney disease stage 3"?',
    gradient: 'from-green-500/10 to-green-600/10 hover:from-green-500/20 hover:to-green-600/20',
  },
  {
    icon: Stethoscope,
    title: 'Medical Necessity',
    description: 'Generate justification letters',
    prompt:
      'Write a medical necessity letter for a patient needing an MRI of the lumbar spine. Patient has chronic lower back pain for 6 months, failed conservative treatment (PT and NSAIDs), and has radiculopathy symptoms.',
    gradient: 'from-orange-500/10 to-orange-600/10 hover:from-orange-500/20 hover:to-orange-600/20',
  },
  {
    icon: DollarSign,
    title: 'Eligibility Check',
    description: 'Verify coverage details',
    prompt:
      'How do I verify if a patient with Blue Cross Blue Shield has coverage for outpatient physical therapy? What information do I need to collect?',
    gradient: 'from-teal-500/10 to-teal-600/10 hover:from-teal-500/20 hover:to-teal-600/20',
  },
];

export const PRIOR_AUTH_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: ClipboardCheck,
    title: 'Procedure PA Check',
    description: 'Review payer authorization rules',
    prompt:
      'What are the prior authorization requirements for a lumbar spine MRI (CPT 72148) for a UnitedHealthcare Medicare Advantage patient with chronic radiculopathy?',
    gradient: 'from-blue-500/10 to-blue-600/10 hover:from-blue-500/20 hover:to-blue-600/20',
  },
  {
    icon: FileSearch,
    title: 'Submission Checklist',
    description: 'Build required documentation',
    prompt:
      'Generate a prior authorization submission checklist for bilateral knee viscosupplementation injections under Aetna PPO.',
    gradient: 'from-indigo-500/10 to-indigo-600/10 hover:from-indigo-500/20 hover:to-indigo-600/20',
  },
  {
    icon: ShieldCheck,
    title: 'Payer Policy Review',
    description: 'Analyze approval criteria',
    prompt:
      'Review Medicare prior auth criteria for cervical epidural steroid injection and tell me what clinical documentation is required.',
    gradient: 'from-cyan-500/10 to-cyan-600/10 hover:from-cyan-500/20 hover:to-cyan-600/20',
  },
];

export const CODING_EXPERT_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: FileText,
    title: 'Procedure Code Lookup',
    description: 'Find accurate CPT billing',
    prompt:
      'What CPT and HCPCS codes should be billed for ultrasound-guided trigger point injections to 3 muscle groups?',
    gradient: 'from-purple-500/10 to-purple-600/10 hover:from-purple-500/20 hover:to-purple-600/20',
  },
  {
    icon: Search,
    title: 'Diagnosis Mapping',
    description: 'Convert diagnosis to ICD-10',
    prompt:
      'Map the diagnosis "chronic bilateral low back pain with left-sided sciatica due to lumbar disc herniation" to the most accurate ICD-10 codes.',
    gradient: 'from-green-500/10 to-green-600/10 hover:from-green-500/20 hover:to-green-600/20',
  },
  {
    icon: Calculator,
    title: 'Modifier Validation',
    description: 'Check coding combinations',
    prompt:
      'Can CPT 20553 and CPT 76942 be billed together on the same date of service, and which modifiers are required?',
    gradient:
      'from-fuchsia-500/10 to-fuchsia-600/10 hover:from-fuchsia-500/20 hover:to-fuchsia-600/20',
  },
];

export const MEDICAL_NECESSITY_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: Stethoscope,
    title: 'MRI Justification',
    description: 'Generate medical necessity letter',
    prompt:
      'Write a medical necessity letter for a lumbar MRI for a patient with chronic lower back pain, failed PT, failed NSAIDs, and progressive left leg numbness.',
    gradient: 'from-orange-500/10 to-orange-600/10 hover:from-orange-500/20 hover:to-orange-600/20',
  },
  {
    icon: HeartPulse,
    title: 'Procedure Necessity',
    description: 'Support treatment approval',
    prompt:
      'Create a medical necessity statement for bilateral sacroiliac joint injections after failure of oral medication and home exercise therapy.',
    gradient: 'from-amber-500/10 to-amber-600/10 hover:from-amber-500/20 hover:to-amber-600/20',
  },
  {
    icon: FileCheck,
    title: 'Insurer Documentation',
    description: 'Strengthen payer submission',
    prompt:
      'Draft insurer-facing medical necessity documentation for EMG/NCS testing in a diabetic patient with worsening neuropathic symptoms.',
    gradient: 'from-yellow-500/10 to-yellow-600/10 hover:from-yellow-500/20 hover:to-yellow-600/20',
  },
];

export const CLAIM_ERROR_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: AlertCircle,
    title: 'Claim Audit',
    description: 'Detect coding mismatches',
    prompt:
      'Review this claim for errors: CPT 99214 billed with diagnosis Z00.00 and no supporting chronic condition diagnoses. Insurance is Cigna PPO.',
    gradient: 'from-red-500/10 to-red-600/10 hover:from-red-500/20 hover:to-red-600/20',
  },
  {
    icon: ShieldAlert,
    title: 'Denial Risk Scan',
    description: 'Catch likely payer denials',
    prompt:
      'Analyze this outpatient pain management claim for denial risks: CPT 64483, CPT 77003, ICD-10 M54.16, Medicare patient.',
    gradient: 'from-rose-500/10 to-rose-600/10 hover:from-rose-500/20 hover:to-rose-600/20',
  },
  {
    icon: BadgeX,
    title: 'Modifier Review',
    description: 'Validate billing details',
    prompt:
      'Check whether this claim has modifier issues: CPT 20553 billed with modifier 59 and CPT 97110 on the same DOS.',
    gradient: 'from-pink-500/10 to-pink-600/10 hover:from-pink-500/20 hover:to-pink-600/20',
  },
];

export const APPEAL_LETTER_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: FileWarning,
    title: 'Denied MRI Appeal',
    description: 'Generate insurer appeal letter',
    prompt:
      'Write an appeal letter for a denied lumbar MRI. Reason for denial: insufficient conservative treatment. Patient completed 8 weeks PT and failed NSAIDs.',
    gradient: 'from-violet-500/10 to-violet-600/10 hover:from-violet-500/20 hover:to-violet-600/20',
  },
  {
    icon: RefreshCcw,
    title: 'Claim Reconsideration',
    description: 'Challenge payer denial',
    prompt:
      'Draft a reconsideration appeal for denied trigger point injections due to alleged lack of medical necessity.',
    gradient: 'from-purple-500/10 to-purple-600/10 hover:from-purple-500/20 hover:to-purple-600/20',
  },
  {
    icon: ShieldBan,
    title: 'Authorization Appeal',
    description: 'Respond to PA rejection',
    prompt:
      'Generate a prior authorization appeal letter for denied EMG/NCS testing in a patient with progressive neuropathy.',
    gradient: 'from-indigo-500/10 to-indigo-600/10 hover:from-indigo-500/20 hover:to-indigo-600/20',
  },
];

export const SOAP_WRITER_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: NotebookPen,
    title: 'Visit Note to SOAP',
    description: 'Convert rough notes instantly',
    prompt:
      'Convert these rough visit notes into a complete SOAP note: patient reports worsening low back pain, left leg numbness, failed ibuprofen, positive straight leg raise.',
    gradient: 'from-sky-500/10 to-sky-600/10 hover:from-sky-500/20 hover:to-sky-600/20',
  },
  {
    icon: ClipboardPen,
    title: 'Assessment & Plan',
    description: 'Build clinical structure',
    prompt:
      'Generate Assessment and Plan sections for a follow-up chronic cervical radiculopathy visit after partial response to PT.',
    gradient: 'from-blue-400/10 to-blue-500/10 hover:from-blue-400/20 hover:to-blue-500/20',
  },
  {
    icon: ScrollText,
    title: 'Follow-up Note',
    description: 'Draft provider documentation',
    prompt:
      'Write a SOAP follow-up note for chronic bilateral knee osteoarthritis with persistent pain despite oral NSAIDs.',
    gradient: 'from-cyan-400/10 to-cyan-500/10 hover:from-cyan-400/20 hover:to-cyan-500/20',
  },
];

export const PATHOLOGY_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: Microscope,
    title: 'Pathology Summary',
    description: 'Interpret pathology findings',
    prompt:
      'Summarize this pathology report in plain clinical language: invasive ductal carcinoma, grade 2, ER positive, PR positive, HER2 negative.',
    gradient:
      'from-emerald-500/10 to-emerald-600/10 hover:from-emerald-500/20 hover:to-emerald-600/20',
  },
  {
    icon: FlaskConical,
    title: 'Lab Pattern Analysis',
    description: 'Identify clinical significance',
    prompt:
      'Interpret these abnormal labs together: elevated ESR, positive ANA, low complement C3, persistent fatigue, joint pain.',
    gradient: 'from-green-400/10 to-green-500/10 hover:from-green-400/20 hover:to-green-500/20',
  },
  {
    icon: TestTube2,
    title: 'Differential Insight',
    description: 'Suggest possible directions',
    prompt:
      'Review this biopsy result showing chronic inflammatory infiltrate with focal atypia and suggest likely differential considerations.',
    gradient: 'from-lime-500/10 to-lime-600/10 hover:from-lime-500/20 hover:to-lime-600/20',
  },
];

export const DENTAL_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: Smile,
    title: 'Dental Narrative',
    description: 'Generate procedure documentation',
    prompt:
      'Write a dental clinical narrative for crown placement on tooth #30 due to recurrent fracture and failed restoration.',
    gradient: 'from-cyan-500/10 to-cyan-600/10 hover:from-cyan-500/20 hover:to-cyan-600/20',
  },
  {
    icon: Shield,
    title: 'Insurance Dental Claim',
    description: 'Support payer approval',
    prompt:
      'Generate dental insurance documentation for scaling and root planing in all four quadrants with chronic periodontal disease.',
    gradient: 'from-teal-500/10 to-teal-600/10 hover:from-teal-500/20 hover:to-teal-600/20',
  },
  {
    icon: FilePlus2,
    title: 'Procedure Coding',
    description: 'Build dental billing note',
    prompt:
      'Create a billing-ready clinical note for extraction of impacted wisdom tooth #17 with sedation.',
    gradient: 'from-blue-500/10 to-blue-600/10 hover:from-blue-500/20 hover:to-blue-600/20',
  },
];

export const ELIGIBILITY_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: DollarSign,
    title: 'Coverage Verification',
    description: 'Check payer eligibility workflow',
    prompt:
      'How do I verify whether Blue Cross Blue Shield covers outpatient physical therapy visits for this patient and what benefits should I confirm?',
    gradient: 'from-teal-500/10 to-teal-600/10 hover:from-teal-500/20 hover:to-teal-600/20',
  },
  {
    icon: BadgeDollarSign,
    title: 'Benefit Breakdown',
    description: 'Understand patient responsibility',
    prompt:
      'Walk me through verifying deductible, copay, coinsurance, and authorization requirements for an Aetna PPO pain management patient.',
    gradient: 'from-cyan-500/10 to-cyan-600/10 hover:from-cyan-500/20 hover:to-cyan-600/20',
  },
  {
    icon: Landmark,
    title: 'Medicare Eligibility',
    description: 'Review federal payer details',
    prompt:
      'What exact eligibility information should I collect before scheduling EMG/NCS testing for a Medicare patient?',
    gradient: 'from-sky-500/10 to-sky-600/10 hover:from-sky-500/20 hover:to-sky-600/20',
  },
];

export const OPERATIVE_NOTE_STARTER_PROMPTS: StarterPrompt[] = [
  {
    icon: Scissors,
    title: 'Procedure Note',
    description: 'Draft operative report',
    prompt:
      'Write an operative note for ultrasound-guided bilateral knee corticosteroid injections performed under sterile technique without complications.',
    gradient: 'from-slate-500/10 to-slate-600/10 hover:from-slate-500/20 hover:to-slate-600/20',
  },
  {
    icon: Syringe,
    title: 'Injection Summary',
    description: 'Generate procedural details',
    prompt:
      'Draft a detailed procedure note for left L5 transforaminal epidural steroid injection with fluoroscopic guidance.',
    gradient: 'from-gray-500/10 to-gray-600/10 hover:from-gray-500/20 hover:to-gray-600/20',
  },
  {
    icon: ClipboardSignature,
    title: 'Post-op Documentation',
    description: 'Create complete operative narrative',
    prompt:
      'Generate a post-procedure operative note for trigger point injections to cervical paraspinal and trapezius muscles.',
    gradient: 'from-zinc-500/10 to-zinc-600/10 hover:from-zinc-500/20 hover:to-zinc-600/20',
  },
];
